import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { FileText, Download, X } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { renderAgreementForPlan } from '../../lib/lease';
import { downloadAgreementPdf } from '../../lib/agreementPdf';
import { LeaseDocument } from '../../components/LeaseDocument';
import { PageHeader } from '../../components/PageHeader';
import { Spinner } from '../../components/Spinner';
import type { UserDoc } from '../../lib/types';

interface SignatureRec {
  signatureId: string;
  signerName?: string;
  signerEmail?: string;
  agreementText: string;
  agreementVersion?: string;
  agreementHash?: string;
  signatureImageRef?: string;
  ipAddress?: string;
  signedAt?: { toDate: () => Date };
}

/** A lease PDF filed against a pilot — the DocuSign path. */
interface LeaseDocRec {
  documentId: string;
  ownerUid: string;
  label?: string;
  fileRef?: string;
  fileName?: string;
  signedDate?: string;
  verificationStatus?: string;
  uploadedAt?: { toDate: () => Date };
}

const fmtDate = (d?: { toDate: () => Date }) =>
  d?.toDate ? d.toDate().toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—';

function isPromo(text: string) {
  return text.includes('PROMO PLAN ADDENDUM');
}

export function AdminAgreements() {
  const { demoMode } = useAuth();
  const [sigs, setSigs] = useState<SignatureRec[] | null>(null);
  const [leaseDocs, setLeaseDocs] = useState<LeaseDocRec[] | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [viewing, setViewing] = useState<SignatureRec | null>(null);

  useEffect(() => {
    if (demoMode) {
      const text = renderAgreementForPlan(
        { pilotName: 'Brian Matthews', pilotAddress: '88 Marina Way, Long Beach, CA 90803', signDate: '2026-05-22' },
        'standard'
      );
      setSigs([
        {
          signatureId: 'demo-1',
          signerName: 'Brian Matthews',
          signerEmail: 'brian@example.com',
          agreementText: text,
          agreementVersion: 'ajm-dry-lease-draft-5-2026-06-07',
          agreementHash: 'demo0000hashvalue',
          signedAt: { toDate: () => new Date('2026-05-22T17:04:00') },
        },
      ]);
      setLeaseDocs([]);
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(query(collection(db, 'leaseSignatures'), orderBy('signedAt', 'desc')), (snap) =>
      setSigs(snap.docs.map((d) => ({ ...(d.data() as SignatureRec), signatureId: d.id })))
    );
    // Leases executed in DocuSign are filed as documents of type 'lease', not as
    // leaseSignatures — so this page only ever showed the one pilot who signed
    // in-app. Both are agreements; both belong here.
    const unsubDocs = onSnapshot(
      query(collection(db, 'documents'), where('type', '==', 'lease')),
      (snap) => setLeaseDocs(snap.docs.map((d) => ({ ...(d.data() as LeaseDocRec), documentId: d.id })))
    );
    const unsubUsers = onSnapshot(collection(db, 'users'), (snap) => {
      const m: Record<string, string> = {};
      snap.docs.forEach((d) => {
        const u = d.data() as UserDoc;
        m[d.id] =
          `${u.personal?.firstName ?? ''} ${u.personal?.lastName ?? ''}`.trim() || u.email || 'Pilot';
      });
      setNames(m);
    });
    return () => {
      unsub();
      unsubDocs();
      unsubUsers();
    };
  }, [demoMode]);

  function download(s: SignatureRec) {
    const safe = (s.signerName || 'lessee').replace(/[^A-Za-z0-9]+/g, '-');
    downloadAgreementPdf(s.agreementText, `AJM-Agreement-${safe}.pdf`);
  }

  return (
    <>
      <PageHeader title="Agreements" subtitle="Every signed lease — view the document or download a copy." />

      {!sigs || !leaseDocs ? (
        <Spinner label="Loading agreements" />
      ) : sigs.length === 0 && leaseDocs.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">No signed agreements yet.</div>
      ) : (
        <div className="space-y-3">
          {/* DocuSign-executed leases. The PDF is the document — there is no
              in-app text to render, so these open the filed copy directly. */}
          {[...leaseDocs]
            .sort((a, b) => (b.signedDate ?? '').localeCompare(a.signedDate ?? ''))
            .map((d) => (
              <div key={d.documentId} className="card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-base font-semibold text-navy-900">
                      {names[d.ownerUid] ?? 'Pilot'}
                    </div>
                    <div className="mt-0.5 text-xs text-ink-muted">
                      Signed {d.signedDate ?? fmtDate(d.uploadedAt)} · DocuSign
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800">
                    Standard
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  <a
                    href={d.fileRef}
                    target="_blank"
                    rel="noopener"
                    className="btn-secondary inline-flex min-h-0 items-center gap-1.5 px-3 py-1.5 text-xs"
                  >
                    <FileText size={13} /> View signed PDF
                  </a>
                </div>
              </div>
            ))}

          {sigs.map((s) => (
            <div key={s.signatureId} className="card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-base font-semibold text-navy-900">{s.signerName || 'Pilot'}</div>
                  <div className="mt-0.5 text-xs text-ink-muted">
                    Signed {fmtDate(s.signedAt)}
                    {s.agreementVersion ? ` · ${s.agreementVersion}` : ''}
                  </div>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    isPromo(s.agreementText) ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'
                  }`}
                >
                  {isPromo(s.agreementText) ? 'Promo plan' : 'Standard'}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                <button
                  onClick={() => setViewing(s)}
                  className="btn-secondary inline-flex min-h-0 items-center gap-1.5 px-3 py-1.5 text-xs"
                >
                  <FileText size={13} /> View
                </button>
                <button
                  onClick={() => download(s)}
                  className="btn-secondary inline-flex min-h-0 items-center gap-1.5 px-3 py-1.5 text-xs"
                >
                  <Download size={13} /> Download PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-navy-900/40 p-4 backdrop-blur-sm">
          <div className="my-6 w-full max-w-2xl">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-white drop-shadow">
                {viewing.signerName} — signed {fmtDate(viewing.signedAt)}
              </span>
              <button onClick={() => setViewing(null)} className="rounded-full bg-white/90 p-1.5 text-navy-900 hover:bg-white">
                <X size={16} />
              </button>
            </div>
            <LeaseDocument text={viewing.agreementText} className="max-h-[70vh]" />
            <div className="mt-2 rounded-lg bg-white p-4 text-xs text-ink-muted">
              <div className="mb-2 font-semibold text-navy-900">Signature on file</div>
              {viewing.signatureImageRef && (
                <img
                  src={viewing.signatureImageRef}
                  alt="Signature"
                  className="mb-2 h-16 w-auto rounded border border-slate-200 bg-white"
                />
              )}
              <div>Signed: {fmtDate(viewing.signedAt)}</div>
              {viewing.ipAddress && <div>IP: {viewing.ipAddress}</div>}
              {viewing.agreementHash && <div className="break-all font-mono">SHA-256: {viewing.agreementHash}</div>}
              <button onClick={() => download(viewing)} className="btn-secondary mt-3 inline-flex items-center gap-1.5 text-xs">
                <Download size={13} /> Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
